// ---------------------------------------------------------------------------
// Volume chart — simple div-based bar chart with hover tooltips
// ---------------------------------------------------------------------------

import { useState } from 'react';
import { useFormatters } from '../hooks/useFormatters';

interface VolumeDataPoint {
  /** Label for the x-axis (e.g. date). */
  label: string;
  /** Numeric value for the bar height. */
  value: number;
}

interface VolumeChartProps {
  /** Array of data points to display as bars. */
  data: VolumeDataPoint[];
  /** Chart title. */
  title?: string;
}

export function VolumeChart({ data, title = 'Daily Volume' }: VolumeChartProps) {
  const f = useFormatters();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const maxValue = data.length > 0 ? Math.max(...data.map((d) => d.value), 1) : 1;

  return (
    <div className="bg-surface-raised rounded-sm border border-hairline p-6">
      <h3 className="font-mono text-[0.6875rem] font-medium text-ink-muted uppercase tracking-[0.08em] mb-6">
        {title}
      </h3>

      {data.length === 0 ? (
        <p className="text-sm text-ink-muted text-center py-8">
          No volume data available for this period.
        </p>
      ) : (
        <div className="relative">
          {/* Chart area */}
          <div className="flex items-end gap-1 h-48">
            {data.map((point, i) => {
              const heightPct = Math.max((point.value / maxValue) * 100, 2);
              const isHovered = hoveredIndex === i;

              return (
                <div
                  key={`${point.label}-${i}`}
                  className="flex-1 flex flex-col items-center justify-end relative"
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  {/* Tooltip */}
                  {isHovered && (
                    <div className="absolute bottom-full mb-2 z-10 bg-ink-surface text-ink-invert text-xs rounded-sm px-2.5 py-1.5 whitespace-nowrap shadow-md">
                      <div className="font-medium">{point.label}</div>
                      <div className="text-ink-muted font-mono">
                        {f.number(point.value)} verifications
                      </div>
                    </div>
                  )}

                  {/* Bar */}
                  <div
                    className={`w-full rounded-t-sm transition-colors ${
                      isHovered ? 'bg-primary-hover' : 'bg-primary'
                    }`}
                    style={{ height: `${heightPct}%`, minHeight: '2px' }}
                    role="img"
                    aria-label={`${point.label}: ${point.value}`}
                  />
                </div>
              );
            })}
          </div>

          {/* X-axis labels (show subset to avoid crowding) */}
          <div className="flex gap-1 mt-2">
            {data.map((point, i) => {
              // Show every Nth label depending on data length
              const showLabel =
                data.length <= 14 ||
                i === 0 ||
                i === data.length - 1 ||
                i % Math.ceil(data.length / 7) === 0;

              return (
                <div key={`label-${point.label}-${i}`} className="flex-1 text-center">
                  {showLabel && (
                    <span className="text-xs text-ink-muted font-mono">{point.label}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
